import { FC, MouseEvent, useCallback, useState } from 'react';
import clsx from 'clsx';
import { Tooltip, IconButton, CircularProgress } from '@mui/material';
import { Check, Close } from '@mui/icons-material';

import { RBCEvent } from '../types';
import { useCalendarContext } from '../../../../data-access';
import './SuggestedTaskActions.scss';
import { useTranslation } from 'react-i18next';

interface SuggestedTaskActionsProps {
  event: Partial<RBCEvent>;
  className?: string;
  onDiscard: (eventId: string) => void;
  onAccept: (event: Partial<RBCEvent>, calendarId: string) => void;
}

const SuggestedTaskActions: FC<SuggestedTaskActionsProps> = ({
  event,
  className,
  onDiscard,
  onAccept,
}) => {
  const { t } = useTranslation();
  const { mainCalendar } = useCalendarContext();

  const [loading, setLoading] = useState<'accept' | 'discard' | null>(null);

  const isActionDisabled = loading !== null;

  const handleAccept = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (isActionDisabled) {
        return;
      }

      onAccept(event, mainCalendar?.calendarId);
    },
    [onAccept]
  );

  const handleDiscard = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (isActionDisabled) {
        return;
      }

      if (!event.eventId) {
        return;
      }
      onDiscard(event.eventId);
    },
    [event.eventId, isActionDisabled, onDiscard]
  );

  return (
    <div className={clsx('suggested-task-actions', className)}>
      <Tooltip title={t('calendar.suggested.acceptToCalendar')}>
        <span>
          <IconButton
            size="small"
            className={clsx(
              'suggested-task-actions__button',
              'suggested-task-actions__button--accept'
            )}
            onClick={handleAccept}
            disabled={isActionDisabled}
          >
            {loading === 'accept' ? (
              <CircularProgress size={10} />
            ) : (
              <Check fontSize="small" className="suggested-task-actions__button__icon--accept" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('calendar.common.discard')}>
        <span>
          <IconButton
            size="small"
            className={clsx(
              'suggested-task-actions__button',
              'suggested-task-actions__button--discard'
            )}
            onClick={handleDiscard}
            disabled={isActionDisabled}
          >
            {loading === 'discard' ? (
              <CircularProgress size={10} />
            ) : (
              <Close fontSize="small" className="suggested-task-actions__button__icon--discard" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
};

export default SuggestedTaskActions;
